import { Component, inject, Inject, OnInit } from '@angular/core';
import { ThemeService } from '../../../services/theme/theme.service';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DOCUMENT, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { Button } from 'primeng/button';
import { GlobalsStore } from '../../../stores/globals.state';

export type ThemeCssClass = 'hyperion-light' | 'hyperion-dark';

@Component({
  selector: 'app-theme-switcher',
  standalone: true,
  imports: [
    InputSwitchModule,
    FormsModule,
    NgClass,
    ToggleButtonModule,
    Button,
  ],
  templateUrl: './theme-switcher.component.html',
  styleUrl: './theme-switcher.component.scss',
})
export class ThemeSwitcherComponent implements OnInit {
  readonly globalsStore = inject(GlobalsStore);

  lightTheme: ThemeCssClass = 'hyperion-light';
  darkTheme: ThemeCssClass = 'hyperion-dark';
  activeTheme: ThemeCssClass = this.lightTheme;
  localStorage: Storage | undefined;

  constructor(
    private themeService: ThemeService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.localStorage = document.defaultView?.localStorage;
  }

  /**
   * Initializes the component when it is created. This method assigns the currently selected theme
   * based on the local storage value or falls back to a default theme. It then applies the theme
   * through the themeService.
   *
   * @return {void} Does not return a value.
   */
  ngOnInit(): void {
    this.activeTheme = (this.localStorage?.getItem('hyperion-theme') ||
      this.lightTheme) as ThemeCssClass;
    this.themeService.switchTheme(this.activeTheme);
    this.globalsStore.setTheme(this.activeTheme);
  }

  /**
   * Toggles the current theme between light and dark mode.
   * Updates the internal active theme state, applies the new theme using the theme service,
   * stores the preference in local storage, and updates the HTML `className` accordingly.
   *
   * @return {void} Does not return a value.
   */
  changeTheme(): void {
    this.activeTheme =
      this.activeTheme === this.lightTheme ? this.darkTheme : this.lightTheme;
    this.themeService.switchTheme(this.activeTheme);
    this.localStorage?.setItem('hyperion-theme', this.activeTheme);
    document.documentElement.className = this.activeTheme; // Update HTML class
    this.globalsStore.setTheme(this.activeTheme);
  }
}
